import {
  Box,
  Drawer,
  DrawerContent,
  Flex,
  Portal,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FiExternalLink,
  FiHome,
  FiInfo,
  FiTool,
  FiUserPlus,
  FiUser,
} from "react-icons/fi";
import { type PropsWithChildren } from "react";
import { isBrowser } from "react-device-detect";
import { useLoggedInUserInfo } from "../common/services/getLoggedInUserInfo.ts";
import { LoadingPage } from "../common/components/LoadingPage.tsx";
import { AdminPanel } from "../common/components/AdminPanel/AdminPanel.tsx";
import posthog from "posthog-js";
import { AiFillCalculator } from "react-icons/ai";

import Footer from "./components/Footer";
import SidebarContent from "./components/SidebarContent";
import MobileNav from "./components/MobileNav";
import { type LinkItemProps } from "./components/NavItems";
import { SmsOptInNudge } from "../common/components/SmsOptInNudge.tsx";

export function Layout({ children }: PropsWithChildren) {
  const { isPending, error, userInfo: user } = useLoggedInUserInfo();

  const linkItems: Array<LinkItemProps> = [
    { name: "Dashboard", icon: FiHome, path: "/" },
  ];
  linkItems.push({
    name: "Profile",
    icon: FiUser,
    path: "/profile",
  });
  linkItems.push({
    name: "About Us",
    icon: FiInfo,
    path: "/about-us",
  });
  if (user?.roles?.includes("Worker")) {
    // linkItems.push({
    //   name: "Training",
    //   icon: FiBook,
    //   path: "/referrals",
    // });
    // linkItems.push({
    //   name: "Time-Off",
    //   icon: FiCalendar,
    //   path: "/referrals",
    // });
  }

  if (user?.roles?.includes("Sales Manager")) {
    linkItems.push({
      name: "Manage Users",
      icon: FiUserPlus,
      path: "/users",
    });
    // linkItems.push({
    //   name: "Training",
    //   icon: FiBook,
    //   path: "/referrals",
    // });
  }

  if (user?.roles?.includes("Customer")) {
    linkItems.push({
      name: "Request Service",
      icon: FiTool,
      path: "/request",
    });
    // linkItems.push({
    //   name: "Documents",
    //   icon: FiFileText,
    //   path: "/docs",
    // });
    // linkItems.push({
    //   name: "Refer-A-Friend",
    //   icon: FiUsers,
    //   path: "/referrals",
    // });
  }

  if (
    user?.roles?.includes("Sales Rep") ||
    user?.roles?.includes("Admin") ||
    user?.roles?.includes("Sales Manager") ||
    user?.isVIPAdminUser
  ) {
    const tools: LinkItemProps = {
      name: "Tools",
      icon: FiTool,
      children: [
        {
          name: "Schedule Survey",
          icon: FiExternalLink,
          path: "https://book.housecallpro.com/book/The-Light-Group-USA/2a45f7e4a8294fb98581f07f5176e09e",
          external: true,
        },
      ],
    };

    if (
      posthog.isFeatureEnabled("tools-module") ||
      window.location.host.includes("localhost") ||
      window.location.host.includes("192.168.0.143")
    ) {
      tools.children.push({
        path: "/tools/roof-calculator",
        icon: AiFillCalculator,
        name: "Roof Calculator",
      });
    }

    // Always show Deal Initiation for sales-related roles
    tools.children.push({
      path: "/deal-submission",
      icon: FiExternalLink,
      name: "Deal Submission",
    });

    linkItems.push(tools);
  }

  const { open, onOpen, onClose, setOpen } = useDisclosure();

  if (isPending) {
    return <LoadingPage isFullPage={true} />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Box
      height={"100dvh"}
      maxHeight={"100dvh"}
      bg={"bg.muted"}
      position="relative"
      style={{ display: "flex", flexFlow: "column nowrap", overflow: "auto" }}
    >
      <AdminPanel />
      <Flex
        style={{
          maxHeight: "100%",
          height: "100%",
          overflow: "auto",
          flexFlow: !isBrowser ? "column nowrap" : "row nowrap",
        }}
      >
        {isBrowser && (
          <>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexFlow: "column nowrap",
                minHeight: "0",
              }}
            >
              <SidebarContent
                onClose={onClose}
                display={{ base: "none", md: "block" }}
                linkItems={linkItems}
              />
            </div>
            <Box
              className={"main-content-area"}
              p="4"
              style={{
                display: "flex",
                flexFlow: "column nowrap",
                justifyContent: "space-between",
                padding: 0,
                overflowY: "auto",
                width: "100%",
              }}
            >
              <Stack style={{ flex: 1, display: "flex" }}>
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flex: 1,
                    flexFlow: "column nowrap",
                  }}
                >
                  {children}
                </div>
              </Stack>
              <Footer />
            </Box>
          </>
        )}
        {!isBrowser && (
          <>
            <Stack style={{ flex: 1 }}>
              <Drawer.Root
                open={open}
                placement="start"
                onOpenChange={({ open }) => setOpen(open)}
                restoreFocus={false}
                size="full"
              >
                <Portal>
                  <Drawer.Positioner>
                    <DrawerContent>
                      <SidebarContent onClose={onClose} linkItems={linkItems} />
                    </DrawerContent>
                  </Drawer.Positioner>
                </Portal>
              </Drawer.Root>
              <MobileNav display={{ base: "flex" }} onOpen={onOpen} />
              <Box
                p="4"
                pb="60px"
                style={{
                  display: "flex",
                  flexFlow: "column nowrap",
                  justifyContent: "space-between",
                  paddingTop: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                  overflowY: "auto",
                  position: "relative",
                  flex: 1,
                }}
              >
                {children}
              </Box>
            </Stack>
            <Footer />
          </>
        )}
      </Flex>
      <SmsOptInNudge userInfo={user} />
    </Box>
  );
}
